/**
 * Resolve a card (game + name + number [+ set]) to its canonical TCGplayer
 * product in OUR OWN catalog (catalog_products, synced nightly from TCGCSV).
 *
 * This is the bridge between "what the user picked in a lookup" and the
 * catalog identity that server-side pricing joins on. Used by:
 *   - quickCardLookup (on pick: best-effort resolve -> hidden form field)
 *   - bulk scan auto-accept (scan -> CardSight -> unique match -> auto-save)
 *   - the one-time backfill of existing lots (Phase 1d, done in SQL)
 *
 * Matching strategy (deliberately conservative — a wrong ID silently prices
 * the wrong card, no ID just means "resolve later"):
 *   1. category from game, name ilike, up to 50 rows
 *   2. client-side: normalized card-number equality ("058/189" == "58")
 *   3. if several remain and a set name is given, filter by group name
 *   4. return a match only when exactly ONE candidate survives
 */
import { sb } from "./supabase";

/** App "Game" value -> TCGplayer category id (matches sync-tcgcsv CATEGORIES). */
export const GAME_TO_CATEGORY: Record<string, number> = {
  "Pokemon": 3,
  "Pokemon (Japan)": 85,   // added 2026-08-30
  "Magic: The Gathering": 1,
  "Yu-Gi-Oh": 2,
  "One Piece": 68,
  "Riftbound": 89,
  "Lorcana": 71,
  "Flesh & Blood": 62,
  "Dragon Ball Fusion World": 80,
};

/** Inverse of GAME_TO_CATEGORY: TCGplayer category id -> app "Game" value. */
export const CATEGORY_TO_GAME: Record<number, string> = Object.fromEntries(
  Object.entries(GAME_TO_CATEGORY).map(([g, id]) => [id, g]),
);

/** "058/189" -> "58", "TG05/TG30" -> "tg05". Same rules as quickCardLookup. */
export function normalizeCardNumber(n: string | null | undefined): string {
  return String(n || "").trim().split("/")[0].replace(/^0+(?=\d)/, "").toLowerCase();
}

export interface CatalogMatch {
  productId: number;
  name: string;
  cardNumber: string | null;
  imageUrl: string | null;
  groupName: string | null;
}

interface CatalogRow {
  product_id: number;
  name: string;
  card_number: string | null;
  image_url: string | null;
  sealed_groups: { name: string } | null;
}

export async function resolveCatalogProduct(opts: {
  game: string;
  name: string;
  number?: string;
  set?: string;
}): Promise<CatalogMatch | null> {
  const category = GAME_TO_CATEGORY[opts.game];
  const name = (opts.name || "").trim();
  if (!category || !name) return null;

  let rows: CatalogRow[] = [];
  try {
    const { data, error } = await sb
      .from("catalog_products")
      .select("product_id,name,card_number,image_url,sealed_groups(name)")
      .eq("category_id", category)
      .ilike("name", `%${name.replace(/[%_]/g, "")}%`)
      .limit(50);
    if (error) throw error;
    rows = (data || []) as unknown as CatalogRow[];
  } catch (e) {
    console.warn("[catalogResolve] query failed:", e);
    return null;
  }
  if (!rows.length) return null;

  // Narrow by normalized card number when we have one.
  let candidates = rows;
  const want = normalizeCardNumber(opts.number);
  if (want) {
    const byNumber = rows.filter(r => normalizeCardNumber(r.card_number) === want);
    if (byNumber.length) candidates = byNumber;
    else return null; // a number was given but nothing matches it — don't guess
  }

  // Narrow by set/group name when still ambiguous.
  if (candidates.length > 1 && (opts.set || "").trim()) {
    const setLc = opts.set!.trim().toLowerCase();
    const bySet = candidates.filter(r => {
      const gn = (r.sealed_groups?.name || "").toLowerCase();
      return gn.includes(setLc) || setLc.includes(gn);
    });
    if (bySet.length) candidates = bySet;
  }

  if (candidates.length !== 1) return null;   // ambiguous — resolve later
  const m = candidates[0]!;
  return {
    productId: m.product_id,
    name: m.name,
    cardNumber: m.card_number,
    imageUrl: m.image_url,
    groupName: m.sealed_groups?.name ?? null,
  };
}

/* ============================================================
   Current market price for a catalog product (client-side twin
   of the revalue_inventory() variant ladder).
   ============================================================ */

/** Default variant ladder — MUST stay in sync with the `prio` array in
 *  public.revalue_inventory() (migration variant_ladder_prefer_unlimited,
 *  2026-08-30), or the price shown at pick time won't match the price the
 *  nightly job writes.
 *
 *  Order rationale: generic printings first, then Unlimited, and every
 *  "1st Edition*" tier LAST. 1st Edition is the scarce printing and usually
 *  the pricier one, so defaulting to it silently inflates the books — it must
 *  be an explicit choice (cards.price_variant / sealed.price_variant). The
 *  bug this replaced picked 1st Edition ahead of Unlimited and mispriced
 *  every vintage lot; it could also err LOW where TCGplayer's 1st-Edition
 *  tier is a thin market (Base Set Shadowless Charizard: 1st Ed Holo $359.95
 *  vs Unlimited Holo $2,146.38). */
const VARIANT_PRIORITY = [
  "Holofoil", "Reverse Holofoil", "Normal", "Foil",
  "Unlimited Holofoil", "Unlimited", "Unlimited Edition Normal", "Unlimited Edition Rainbow Foil",
  "Cold Foil", "Rainbow Foil", "Limited",
  "1st Edition Holofoil", "1st Edition", "1st Edition Normal",
  "1st Edition Rainbow Foil", "1st Edition Cold Foil",
];

/** Rank for the default ladder; unknown printings sort after known ones but
 *  before nothing — same 40-ish bucket the SQL uses. */
function variantRank(subTypeName: string): number {
  const i = VARIANT_PRIORITY.indexOf(subTypeName);
  return i < 0 ? 50 : i;
}

/** Sort variants into the ladder order, with a stable name tiebreak (matches
 *  the SQL's `order by <rank>, sub_type_name`). */
function sortVariants<T extends { variant: string }>(rows: T[]): T[] {
  return rows.sort((a, b) =>
    variantRank(a.variant) - variantRank(b.variant) || a.variant.localeCompare(b.variant));
}

export interface CatalogVariant { variant: string; unit: number; }
export interface CatalogPrice { unit: number; variant: string; }

/** Every priced printing/edition of a product, best-default first.
 *  Powers the variant selector on the lot forms and the lookup grid — this is
 *  how a user says "mine is Unlimited, not 1st Edition". */
export async function listCatalogVariants(productId: number): Promise<CatalogVariant[]> {
  if (!(productId > 0)) return [];
  try {
    const { data, error } = await sb
      .from("catalog_current_prices")
      .select("sub_type_name,market_price")
      .eq("product_id", productId);
    if (error) throw error;
    const rows = (data || [])
      .filter((r: any) => r.market_price != null && r.sub_type_name)
      .map((r: any) => ({ variant: String(r.sub_type_name), unit: +Number(r.market_price).toFixed(2) }));
    return sortVariants(rows);
  } catch (e) {
    console.warn("[listCatalogVariants]", e);
    return [];
  }
}

/** Latest market price for a product. With `preferVariant` set, that printing
 *  wins outright (the user's explicit choice); otherwise the default ladder
 *  decides — the same variant the nightly revaluation job would pick.
 *  Null when the catalog has no price yet. */
export async function getCatalogPrice(productId: number, preferVariant?: string | null): Promise<CatalogPrice | null> {
  const variants = await listCatalogVariants(productId);
  if (!variants.length) return null;
  const want = (preferVariant || "").trim();
  const chosen = (want && variants.find(v => v.variant === want)) || variants[0]!;
  return { unit: chosen.unit, variant: chosen.variant };
}

/** Strip TCGplayer group-name prefixes like "SV02: " / "SWSH07: " so bulk-scan
 *  set names roughly match the app's existing API-style set names. */
export function cleanGroupName(g: string | null | undefined): string {
  return String(g || "").replace(/^[A-Z0-9]{2,10}:\s*/, "").trim();
}

/** Catalog names sometimes carry a "- 199/165" suffix; strip it when it
 *  matches the card number so form names stay clean. */
export function cleanCardName(name: string, cardNumber: string | null | undefined): string {
  const n = String(name || "").trim();
  const num = String(cardNumber || "").trim();
  if (num && n.toLowerCase().endsWith(("- " + num).toLowerCase())) {
    return n.slice(0, n.length - num.length - 2).trim();
  }
  return n;
}

/* ============================================================
   Catalog card SEARCH — powers the lookup grid for EVERY game
   (Pokemon included since 2026-07-13; API grid is ID-search-only now).
   ============================================================ */

export interface CatalogSearchRow {
  productId: number;
  name: string;
  cardNumber: string | null;
  imageUrl: string | null;
  groupName: string | null;
  publishedOn: string | null;
  unit: number | null;      // best market price (variant ladder) or null
  /** Printing/edition the `unit` price came from (null when unpriced). */
  variant: string | null;
  /** Every priced printing, ladder order. Length > 1 means the grid must let
   *  the user choose — this is the 1st Edition vs Unlimited case. */
  variants: CatalogVariant[];
}

/** Search catalog_products for a game by free text. Number-looking tokens
 *  narrow by card number (normalized), word tokens must all appear in the
 *  name, and an optional set name filters by sealed-group name server-side
 *  (substring, so plain "Evolving Skies" matches "SWSH07: Evolving Skies").
 *  Results come back newest-set-first with prices attached. */
export async function searchCatalogCards(game: string, query: string, limit = 60, set?: string): Promise<CatalogSearchRow[]> {
  const category = GAME_TO_CATEGORY[game];
  const q = (query || "").trim();
  if (!category || !q) return [];

  const tokens = q.replace(/["%_]/g, "").split(/\s+/).filter(Boolean);
  // A "number token" narrows by card_number; everything else must appear in the
  // NAME. Two shapes used to fall on the wrong side of that line:
  //   - hyphenated numbers ("OP13-037", "DEM6-EN013", "FB04-080") — the class
  //     had no hyphen, so they were searched as names. That broke card-number
  //     narrowing for ~99% of Yu-Gi-Oh and One Piece and all of Dragon Ball
  //     Fusion World; it only ever "worked" where TCGplayer happens to repeat
  //     the number inside the product name.
  //   - four-letter prefixes ("SWSH039") — {0,3} couldn't reach the digits.
  // The /\d/ requirement still keeps hyphenated NAMES (Ho-Oh, Zekrom-GX) out.
  const numTokens  = tokens.filter(t => /^[A-Za-z]{0,4}\d+[A-Za-z0-9/-]*$/.test(t) && /\d/.test(t));
  const wordTokens = tokens.filter(t => !numTokens.includes(t));
  const setClean   = (set || "").replace(/["%_]/g, "").trim();

  let sel = sb.from("catalog_products")
    .select(setClean
      ? "product_id,name,card_number,image_url,sealed_groups!inner(name,published_on)"
      : "product_id,name,card_number,image_url,sealed_groups(name,published_on)")
    .eq("category_id", category)
    // Order by the set's ACTUAL release date, denormalized onto the product
    // (migration catalog_products_group_published_on, trigger-maintained).
    //
    // This used to order by product_id descending as a "newest set first"
    // proxy. That proxy silently broke for Pokemon (Japan): TCGplayer issued
    // English ids as sets released, but backfilled the whole historical JP
    // catalog at once, so 1998 promos outrank 2024 sets. Of the 250 rows a
    // 'pikachu' search pulled, English got 143 from 2023+ sets and 2 from
    // pre-2010 — Japanese got 39 and 60. With 369 JP Pikachus in the catalog,
    // ~119 were unreachable through the UI at any page.
    .order("group_published_on", { ascending: false, nullsFirst: false })
    .order("product_id", { ascending: false })
    .limit(250);
  for (const w of wordTokens) sel = sel.ilike("name", `%${w}%`);
  // Coarse server-side number filter — substring match tolerates leading
  // zeros ("58" finds "058/189" too); the exact normalized narrowing below
  // stays authoritative. This keeps the fetch relevant instead of burning
  // the 250-row cap on rows the client would discard.
  if (numTokens.length) {
    const wantNum = normalizeCardNumber(numTokens[0]);
    if (wantNum) sel = sel.ilike("card_number", `%${wantNum}%`);
  }
  if (setClean) sel = sel.ilike("sealed_groups.name", `%${setClean}%`);

  let rows: any[] = [];
  try {
    const { data, error } = await sel;
    if (error) throw error;
    rows = data || [];
  } catch (e) {
    console.warn("[searchCatalogCards]", e);
    return [];
  }

  // Client-side number narrowing (same tolerance rules as everywhere else).
  if (numTokens.length) {
    const want = normalizeCardNumber(numTokens[0]);
    const filtered = rows.filter(r => normalizeCardNumber(r.card_number) === want);
    if (filtered.length) rows = filtered;
  }

  // Newest set first, then name.
  rows.sort((a, b) => {
    const pa = a.sealed_groups?.published_on || "";
    const pb = b.sealed_groups?.published_on || "";
    return pb.localeCompare(pa) || String(a.name).localeCompare(String(b.name));
  });
  rows = rows.slice(0, limit);

  // Attach prices in one batched query. We keep EVERY priced printing, not
  // just the ladder winner, so the grid can offer the choice (1st Edition vs
  // Unlimited) instead of silently guessing one.
  const ids = rows.map(r => r.product_id);
  const variantsById = new Map<number, CatalogVariant[]>();
  if (ids.length) {
    try {
      const { data } = await sb.from("catalog_current_prices")
        .select("product_id,sub_type_name,market_price")
        .in("product_id", ids);
      for (const p of (data || []) as any[]) {
        if (p.market_price == null || !p.sub_type_name) continue;
        const list = variantsById.get(p.product_id) || [];
        list.push({ variant: String(p.sub_type_name), unit: +Number(p.market_price).toFixed(2) });
        variantsById.set(p.product_id, list);
      }
      variantsById.forEach(sortVariants);
    } catch { /* prices optional */ }
  }

  return rows.map(r => {
    const variants = variantsById.get(r.product_id) || [];
    return {
      productId: r.product_id,
      name: r.name,
      cardNumber: r.card_number,
      imageUrl: r.image_url,
      groupName: r.sealed_groups?.name ?? null,
      publishedOn: r.sealed_groups?.published_on ?? null,
      unit: variants[0]?.unit ?? null,
      variant: variants[0]?.variant ?? null,
      variants,
    };
  });
}
